import {useState} from "react";
import {useNavigate, useParams} from "react-router";
import {ArrowLeft, Check, UserRound} from "lucide-react";
import {useSession} from "@/account/lib/auth-client";
import {trpc} from "@/account/lib/trpc";
import {ENTERPRISE_TRIAL_URL} from "@/shared/lib/planCatalog";

export function NewSubscriptionPage() {
  const navigate = useNavigate();
  const {slug} = useParams<{slug: string}>();
  const {data: session} = useSession();
  const [confirmFree, setConfirmFree] = useState(false);
  const {data: organization, isLoading: organizationLoading} = trpc.organization.getBySlug.useQuery(
    {slug: slug!}, {enabled: Boolean(slug && session)},
  );
  const {data: plans, isLoading, error} = trpc.subscription.getAvailablePlansForNewSubscription.useQuery(
    slug ? {organizationId: organization?.id} : undefined,
    {enabled: Boolean(session && (!slug || organization?.id))},
  );
  const activateFree = trpc.subscription.mutation.createFreeSubscription.useMutation({
    onSuccess: ({subscriptionId}) => navigate(`/subscriptions/${subscriptionId}`),
  });
  const freePlan = plans?.find((plan) => plan.id === "free");
  const canManage = !slug || organization?.currentUserRole === "owner" || organization?.currentUserRole === "admin";
  if (isLoading || (slug && organizationLoading)) return <p role="status">Loading plans…</p>;
  if (!canManage) return <p role="alert">You do not have permission to manage this organization's subscriptions.</p>;
  if (error || !plans) return <p role="alert">Unable to load plans. Please try again.</p>;

  return (
    <div className="account-ui-page subscription-upgrade-page">
      <button type="button" onClick={() => navigate(-1)}><ArrowLeft aria-hidden="true" /> BACK</button>
      <header className="subscription-upgrade__header">
        <h1>CHOOSE YOUR PLAN</h1>
        <p>Free for exploring NOSIBLE. Enterprise for production access and higher limits.</p>
        <div className="subscription-upgrade__current-plan"><UserRound aria-hidden="true" /><span>{slug ? "ORGANIZATION" : "PERSONAL"}</span><strong>{organization?.name || session?.user.name || session?.user.email}</strong></div>
      </header>
      <section className="subscription-upgrade__plans" aria-label="Available plans">
        <article className="subscription-upgrade__plan">
          <h2>Free</h2>
          <p className="subscription-upgrade__description">Start exploring NOSIBLE with basic limits.</p>
          <div className="subscription-upgrade__price"><strong>$0</strong><span>/ MONTH</span></div>
          <ul className="subscription-upgrade__features"><li><Check aria-hidden="true" />Basic API access for exploring NOSIBLE</li>{freePlan && <li><Check aria-hidden="true" />{freePlan.defaultApiKeyLimit} API keys</li>}</ul>
          <div className="subscription-upgrade__card-action">
            {freePlan && !slug ? (confirmFree ? (
              <div className="subscription-upgrade__confirmation">
                <p>Activate your Free subscription?</p>
                <button type="button" disabled={activateFree.isPending} onClick={() => setConfirmFree(false)}>CANCEL</button>
                <button type="button" className="subscription-upgrade__action" disabled={activateFree.isPending || !session?.user.id} onClick={() => {if (session?.user.id) activateFree.mutate({ownerId: session.user.id, planId: "free"});}}>{activateFree.isPending ? "ACTIVATING…" : "CONFIRM FREE PLAN"}</button>
              </div>
            ) : <button type="button" className="subscription-upgrade__action" onClick={() => setConfirmFree(true)}>ACTIVATE FREE PLAN</button>) : <p>{slug ? "Free access is available for personal accounts." : "Manage your existing access from Subscriptions."}</p>}
          </div>
        </article>
        <article className="subscription-upgrade__plan">
          <h2>Enterprise</h2>
          <p className="subscription-upgrade__description">Production access for teams that need higher limits and enterprise support.</p>
          <div className="subscription-upgrade__price"><strong>Tailored</strong><span>PRICING</span></div>
          <ul className="subscription-upgrade__features"><li><Check aria-hidden="true" />Higher limits and technical support</li><li><Check aria-hidden="true" />Bulk access via S3 and on-prem options</li></ul>
          <div className="subscription-upgrade__card-action"><a className="subscription-upgrade__action" href={ENTERPRISE_TRIAL_URL}>START ENTERPRISE TRIAL</a></div>
        </article>
      </section>
      {activateFree.error && <p role="alert">{activateFree.error.message}</p>}
    </div>
  );
}
